import React from 'react';
import { motion } from 'framer-motion';
import { FileText, AlertTriangle, CreditCard, Ban, Scale, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const TermsPage = () => {
  const { language } = useLanguage();

  const sections = language === 'nl' ? [
    {
      icon: FileText,
      title: '1. Algemeen',
      content: [
        'Deze algemene voorwaarden zijn van toepassing op het gebruik van KenmerkCheck.nl en alle diensten die via deze website worden aangeboden.',
        'Door gebruik te maken van onze diensten gaat u akkoord met deze voorwaarden.',
        'KenmerkCheck is een tool voor het decoderen van Nederlandse Belastingdienst betalingskenmerken en is bedoeld voor informatie doeleinden.',
        'Wij behouden ons het recht voor om deze voorwaarden te allen tijde aan te passen. Wijzigingen treden in werking na publicatie op deze pagina.'
      ]
    },
    {
      icon: AlertTriangle,
      title: '2. Disclaimer',
      content: [
        'De informatie die door KenmerkCheck wordt verstrekt is uitsluitend bedoeld ter indicatie en kan niet worden beschouwd als professioneel fiscaal advies.',
        'Hoewel wij streven naar nauwkeurigheid, kunnen wij niet garanderen dat de gedecodeerde informatie in alle gevallen 100% correct is.',
        'KenmerkCheck is geen officieel product van de Belastingdienst en is niet verbonden aan de Nederlandse overheid.',
        'Voor definitieve uitspraken over belastingzaken dient u contact op te nemen met de Belastingdienst of een erkend belastingadviseur.',
        'KenmerkCheck is niet aansprakelijk voor eventuele schade die voortvloeit uit het gebruik van onze diensten.'
      ]
    },
    {
      icon: CreditCard,
      title: '3. Abonnementen en Betaling',
      content: [
        'Gratis account: Beperkte functionaliteit, geen registratie vereist voor basis decodering.',
        'Pro account (€15/maand): Onbeperkt bulk decoderen, CSV upload, export functies.',
        'Team account (€79/maand): Alle Pro functies plus meerdere gebruikers en prioriteit support.',
        'Abonnementen worden maandelijks gefactureerd en kunnen op elk moment worden opgezegd.',
        'Bij opzegging blijft het abonnement actief tot het einde van de betaalde periode.',
        'Restitutie is mogelijk binnen 14 dagen na eerste betaling indien u niet tevreden bent.'
      ]
    },
    {
      icon: Ban,
      title: '4. Verboden Gebruik',
      content: [
        'Het is niet toegestaan om onze diensten te gebruiken voor illegale doeleinden.',
        'Automatisch scrapen of excessief gebruik van de API zonder toestemming is verboden.',
        'Het doorverkopen of herverdelen van onze diensten zonder schriftelijke toestemming is niet toegestaan.',
        'Pogingen om de beveiliging van onze systemen te omzeilen of te verstoren zijn verboden.',
        'Bij overtreding behouden wij ons het recht voor om uw account te beëindigen zonder restitutie.'
      ]
    },
    {
      icon: Scale,
      title: '5. Aansprakelijkheid',
      content: [
        'KenmerkCheck wordt aangeboden "as is" zonder enige garantie.',
        'Wij zijn niet aansprakelijk voor directe of indirecte schade voortvloeiend uit het gebruik van onze diensten.',
        'De maximale aansprakelijkheid is beperkt tot het bedrag dat u heeft betaald voor onze diensten in de afgelopen 12 maanden.',
        'Wij garanderen geen ononderbroken beschikbaarheid van de dienst.',
        'Op deze voorwaarden is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter in Nederland.'
      ]
    },
    {
      icon: Mail,
      title: '6. Contact',
      content: [
        'Voor vragen over deze voorwaarden kunt u contact met ons opnemen:',
        'E-mail: info@kenmerkcheck.nl',
        'Wij streven ernaar om binnen 5 werkdagen te reageren.'
      ]
    }
  ] : [
    {
      icon: FileText,
      title: '1. General',
      content: [
        'These terms and conditions apply to the use of KenmerkCheck.nl and all services offered through this website.',
        'By using our services, you agree to these terms.',
        'KenmerkCheck is a tool for decoding Dutch Tax Authority payment references and is intended for informational purposes.',
        'We reserve the right to modify these terms at any time. Changes take effect upon publication on this page.'
      ]
    },
    {
      icon: AlertTriangle,
      title: '2. Disclaimer',
      content: [
        'The information provided by KenmerkCheck is for indicative purposes only and should not be considered professional tax advice.',
        'Although we strive for accuracy, we cannot guarantee that decoded information is 100% correct in all cases.',
        'KenmerkCheck is not an official product of the Tax Authority and is not affiliated with the Dutch government.',
        'For definitive statements on tax matters, please contact the Tax Authority or a certified tax advisor.',
        'KenmerkCheck is not liable for any damages arising from the use of our services.'
      ]
    },
    {
      icon: CreditCard,
      title: '3. Subscriptions and Payment',
      content: [
        'Free account: Limited functionality, no registration required for basic decoding.',
        'Pro account (€15/month): Unlimited bulk decoding, CSV upload, export features.',
        'Team account (€79/month): All Pro features plus multiple users and priority support.',
        'Subscriptions are billed monthly and can be cancelled at any time.',
        'Upon cancellation, the subscription remains active until the end of the paid period.',
        'Refunds are possible within 14 days of first payment if you are not satisfied.'
      ]
    },
    {
      icon: Ban,
      title: '4. Prohibited Use',
      content: [
        'Using our services for illegal purposes is not permitted.',
        'Automated scraping or excessive API use without permission is prohibited.',
        'Reselling or redistributing our services without written permission is not allowed.',
        'Attempts to bypass or disrupt the security of our systems are forbidden.',
        'In case of violation, we reserve the right to terminate your account without refund.'
      ]
    },
    {
      icon: Scale,
      title: '5. Liability',
      content: [
        'KenmerkCheck is provided "as is" without any warranty.',
        'We are not liable for direct or indirect damages arising from the use of our services.',
        'Maximum liability is limited to the amount you have paid for our services in the past 12 months.',
        'We do not guarantee uninterrupted availability of the service.',
        'These terms are governed by Dutch law. Disputes will be submitted to the competent court in the Netherlands.'
      ]
    },
    {
      icon: Mail,
      title: '6. Contact',
      content: [
        'For questions about these terms, you can contact us:',
        'Email: info@kenmerkcheck.nl',
        'We aim to respond within 5 business days.'
      ]
    }
  ];

  return (
    <div className="py-12 md:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-accent" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-primary dark:text-white mb-4">
            {language === 'nl' ? 'Algemene Voorwaarden' : 'Terms and Conditions'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            {language === 'nl' 
              ? 'Laatst bijgewerkt: december 2025'
              : 'Last updated: December 2025'}
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.section
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-accent" />
                  </div>
                  <h2 className="font-heading font-bold text-xl text-primary dark:text-white">
                    {section.title}
                  </h2>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                      <span className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.section>
          ))}
        </div>

        {/* Acceptance notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl"
        >
          <p className="text-amber-800 dark:text-amber-200 text-sm leading-relaxed">
            <strong>{language === 'nl' ? 'Let op:' : 'Note:'}</strong>{' '}
            {language === 'nl' 
              ? 'Door gebruik te maken van KenmerkCheck gaat u akkoord met deze algemene voorwaarden en ons privacybeleid. Als u niet akkoord gaat, verzoeken wij u om geen gebruik te maken van onze diensten.'
              : 'By using KenmerkCheck, you agree to these terms and conditions and our privacy policy. If you do not agree, please do not use our services.'}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsPage;
